/**
 * Server functions voor de CMS-laag (fase G1).
 * Publiek lezen gebeurt met de publishable key: RLS toont enkel gepubliceerde pagina's.
 * Beheeracties draaien als de ingelogde gebruiker; RLS bewaakt can_write en can_publish.
 */
import { valideerWaarde } from "@/lib/rich-text";
import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import type { Database, Json } from "@/integrations/supabase/types";
import { imageVariants, type DeliveryVariant } from "@/lib/image-delivery";
import { assertActiveUser } from "@/lib/admin-access";
import { readFocal, type FocalPoint } from "@/lib/media-taxonomy";
import { chooseDelivery } from "@/lib/cloudflare-images";
import { loadCloudflareFields, loadDeliveryMode, type CloudflareFields } from "@/lib/media-delivery.server";

type PageRow = Database["public"]["Tables"]["content_pages"]["Row"];
type SectionRow = Database["public"]["Tables"]["content_sections"]["Row"];

/** Eén foto uit een fotoreeks bij een blok. */
export type SectionMedia = {
  mediaId: string;
  url: string | null;
  alt: string;
  caption: string;
  variants?: DeliveryVariant[];
  focal?: FocalPoint | null;
  decorative?: boolean;
  placeholder?: string | null;
};

export type ContentSection = {
  id: string;
  sleutel: string;
  sortOrder: number;
  type: string;
  overline: string;
  titel: string;
  tekst: string;
  opsomming: string[];
  knopTekst: string;
  knopLink: string;
  knop2Tekst: string;
  knop2Link: string;
  mediaId: string | null;
  mediaUrl: string | null;
  mediaVariants?: DeliveryVariant[];
  mediaFocal?: FocalPoint | null;
  alt: string;
  /** Fotoreeks bij dit blok, in volgorde. Leeg betekent: de site toont de foto's uit de code. */
  media: SectionMedia[];
  updatedAt: string;
};

export type ContentPage = {
  id: string;
  sleutel: string;
  lang: string;
  status: "concept" | "gepubliceerd";
  metaTitle: string;
  metaDescription: string;
  ogMediaId: string | null;
  ogUrl: string | null;
  indexeerbaar: boolean;
  updatedAt: string;
  sections: ContentSection[];
};

const PAGE_COLUMNS =
  "id, sleutel, lang, status, meta_title, meta_description, og_media_id, indexeerbaar, updated_at";
const SECTION_COLUMNS =
  "id, page_id, sleutel, sort_order, type, overline, titel, tekst, opsomming, knop_tekst, knop_link, knop2_tekst, knop2_link, media_id, alt, updated_at";


function mapSection(
  row: SectionRow,
  mediaUrls: Map<string, MediaUrlEntry>,
  reeksen: Map<string, SectionMedia[]>,
): ContentSection {
  return {
    id: row.id,
    sleutel: row.sleutel,
    sortOrder: row.sort_order,
    type: row.type,
    overline: row.overline,
    titel: row.titel,
    tekst: row.tekst,
    opsomming: row.opsomming ?? [],
    knopTekst: row.knop_tekst,
    knopLink: row.knop_link,
    knop2Tekst: row.knop2_tekst,
    knop2Link: row.knop2_link,
    mediaId: row.media_id,
    mediaUrl: row.media_id ? (mediaUrls.get(row.media_id)?.url ?? null) : null,
    mediaVariants: row.media_id ? mediaUrls.get(row.media_id)?.variants ?? [] : [],
    mediaFocal: row.media_id ? (mediaUrls.get(row.media_id)?.focal ?? null) : null,
    alt: row.alt,
    media: reeksen.get(row.id) ?? [],
    updatedAt: row.updated_at,
  };

}

function mapPage(
  row: PageRow,
  sections: SectionRow[],
  mediaUrls: Map<string, MediaUrlEntry>,
  reeksen: Map<string, SectionMedia[]>,
): ContentPage {
  return {
    id: row.id,
    sleutel: row.sleutel,
    lang: row.lang,
    status: row.status === "gepubliceerd" ? "gepubliceerd" : "concept",
    metaTitle: row.meta_title,
    metaDescription: row.meta_description,
    ogMediaId: row.og_media_id,
    // Deelbeelden (sociale media) blijven uit de eigen opslag: crawlers vragen geen AVIF of WebP aan.
    ogUrl: row.og_media_id ? (mediaUrls.get(row.og_media_id)?.storedUrl ?? null) : null,
    indexeerbaar: row.indexeerbaar,
    updatedAt: row.updated_at,
    sections: sections
      .map((section) => mapSection(section, mediaUrls, reeksen))
      .sort((a, b) => a.sortOrder - b.sortOrder),
  };
}

/** Publieke client zonder sessie; opaque sb_-keys horen enkel in de apikey-header. */
function publicClient() {
  const key = process.env["SUPABASE_PUBLISHABLE_KEY"]!;
  return createClient<Database>(process.env["SUPABASE_URL"]!, key, {
    auth: { persistSession: false, autoRefreshToken: false },
    global: {
      fetch: (input, init) => {
        const headers = new Headers(init?.headers);
        if (key.startsWith("sb_") && headers.get("Authorization") === `Bearer ${key}`) {
          headers.delete("Authorization");
        }
        headers.set("apikey", key);
        return fetch(input, { ...init, headers });
      },
    },
  });
}

type AnyClient = ReturnType<typeof createClient<Database>>;

type MediaUrlEntry = { url: string; variants: DeliveryVariant[]; storedUrl: string; focal: FocalPoint | null; decorative: boolean; placeholder: string | null };

async function loadMediaUrls(client: AnyClient, ids: (string | null)[]) {
  const unique = [...new Set(ids.filter((id): id is string => Boolean(id)))];
  const map = new Map<string, MediaUrlEntry>();
  if (unique.length === 0) return map;
  const { data } = await client.from("media_assets").select("id, url, variants, width, height, decorative, focal_x, focal_y, placeholder_color").in("id", unique);
  // Leverwijze: zonder Cloudflare blijft alles exact zoals voorheen (enkel url en varianten uit de eigen opslag).
  const mode = await loadDeliveryMode(client);
  const cf = mode.provider === "cloudflare" ? await loadCloudflareFields(client, unique) : new Map<string, CloudflareFields>();
  for (const row of data ?? []) {
    const stored = imageVariants(row.variants);
    const fields = cf.get(row.id);
    const chosen = fields ? chooseDelivery({ ...row, ...fields }, stored, mode) : null;
    map.set(row.id, {
      url: chosen?.url ?? row.url,
      variants: chosen ? imageVariants(chosen.variants) : stored,
      storedUrl: row.url,
      focal: readFocal(row.focal_x, row.focal_y),
      decorative: row.decorative,
      placeholder: row.placeholder_color,
    });
  }
  return map;
}

type SectionMediaRow = { section_id: string; media_id: string; alt: string; caption: string };

async function loadSectionMedia(client: AnyClient, sectionIds: string[]): Promise<SectionMediaRow[]> {
  if (sectionIds.length === 0) return [];
  const { data, error } = await client
    .from("content_section_media")
    .select("section_id, media_id, alt, caption, sort_order")
    .in("section_id", sectionIds)
    .order("sort_order");
  if (error) throw new Error(error.message);
  return (data ?? []) as SectionMediaRow[];
}

async function loadPage(client: AnyClient, sleutel: string, lang: string, onlyPublished: boolean) {
  let query = client.from("content_pages").select(PAGE_COLUMNS).eq("sleutel", sleutel).eq("lang", lang);
  if (onlyPublished) query = query.eq("status", "gepubliceerd");
  const { data: page, error } = await query.maybeSingle();
  if (error) throw new Error(error.message);
  if (!page) return null;

  const { data: sections, error: sectionError } = await client
    .from("content_sections")
    .select(SECTION_COLUMNS)
    .eq("page_id", page.id)
    .order("sort_order");
  if (sectionError) throw new Error(sectionError.message);

  const rows = (sections ?? []) as SectionRow[];
  const reeksRijen = await loadSectionMedia(
    client,
    rows.map((row) => row.id),
  );
  const mediaUrls = await loadMediaUrls(client, [
    page.og_media_id,
    ...rows.map((row) => row.media_id),
    ...reeksRijen.map((row) => row.media_id),
  ]);
  const reeksen = new Map<string, SectionMedia[]>();
  for (const row of reeksRijen) {
    const lijst = reeksen.get(row.section_id) ?? [];
    lijst.push({
      mediaId: row.media_id,
      url: mediaUrls.get(row.media_id)?.url ?? null,
      variants: mediaUrls.get(row.media_id)?.variants ?? [],
      focal: mediaUrls.get(row.media_id)?.focal ?? null,
      decorative: mediaUrls.get(row.media_id)?.decorative ?? false,
      placeholder: mediaUrls.get(row.media_id)?.placeholder ?? null,
      alt: row.alt,
      caption: row.caption,
    });
    reeksen.set(row.section_id, lijst);
  }
  return mapPage(page as PageRow, rows, mediaUrls, reeksen);
}

/** Lege velden van de vertaling worden aangevuld met de Nederlandse tekst. */
function mergeSections(vertaald: ContentSection, bron: ContentSection): ContentSection {
  const kies = (a: string, b: string) => (a.trim() === "" ? b : a);
  return {
    ...vertaald,
    overline: kies(vertaald.overline, bron.overline),
    titel: kies(vertaald.titel, bron.titel),
    tekst: kies(vertaald.tekst, bron.tekst),
    opsomming: vertaald.opsomming.length > 0 ? vertaald.opsomming : bron.opsomming,
    knopTekst: kies(vertaald.knopTekst, bron.knopTekst),
    knopLink: kies(vertaald.knopLink, bron.knopLink),
    knop2Tekst: kies(vertaald.knop2Tekst, bron.knop2Tekst),
    knop2Link: kies(vertaald.knop2Link, bron.knop2Link),
    mediaId: vertaald.mediaId ?? bron.mediaId,
    mediaUrl: vertaald.mediaUrl ?? bron.mediaUrl,
    mediaVariants: (vertaald.mediaUrl ? vertaald.mediaVariants : bron.mediaVariants) ?? [],
    mediaFocal: (vertaald.mediaUrl ? vertaald.mediaFocal : bron.mediaFocal) ?? null,
    alt: kies(vertaald.alt, bron.alt),
    // De beeldkeuze is gedeeld; alleen de beschrijving en het bijschrift zijn per taal.
    media: bron.media.map((foto) => {
      const eigen = vertaald.media.find((rij) => rij.mediaId === foto.mediaId);
      return eigen ? { ...foto, alt: kies(eigen.alt, foto.alt), caption: kies(eigen.caption, foto.caption) } : foto;
    }),
  };
}

/** Vertaalde pagina met terugval per sectie op het Nederlands. */
function mergePages(vertaald: ContentPage, bron: ContentPage): ContentPage {
  const vertaaldeSecties = new Map(vertaald.sections.map((section) => [section.sleutel, section]));
  const sections = bron.sections.map((section) => {
    const eigen = vertaaldeSecties.get(section.sleutel);
    return eigen ? mergeSections(eigen, section) : section;
  });
  for (const section of vertaald.sections) {
    if (!bron.sections.some((row) => row.sleutel === section.sleutel)) sections.push(section);
  }
  return {
    ...vertaald,
    metaTitle: vertaald.metaTitle.trim() === "" ? bron.metaTitle : vertaald.metaTitle,
    metaDescription: vertaald.metaDescription.trim() === "" ? bron.metaDescription : vertaald.metaDescription,
    ogMediaId: vertaald.ogMediaId ?? bron.ogMediaId,
    ogUrl: vertaald.ogUrl ?? bron.ogUrl,
    sections: sections.sort((a, b) => a.sortOrder - b.sortOrder),
  };
}

/**
 * De gepubliceerde inhoud van één pagina.
 * Ontbreekt een vertaalde sectie of een veld daarin, dan komt de Nederlandse tekst in de plaats.
 * Is er helemaal geen rij, dan null: de pagina toont haar tekst uit de code.
 */
export const getPublishedPage = createServerFn({ method: "GET" })
  .inputValidator((data: { sleutel: string; lang?: string }) => ({
    sleutel: String(data.sleutel),
    lang: data.lang ?? "nl",
  }))
  .handler(async ({ data }): Promise<ContentPage | null> => {
    const client = publicClient();
    if (data.lang === "nl") return loadPage(client, data.sleutel, "nl", true);
    const [vertaald, nederlands] = await Promise.all([
      loadPage(client, data.sleutel, data.lang, true),
      loadPage(client, data.sleutel, "nl", true),
    ]);
    if (!vertaald) return nederlands;
    if (!nederlands) return vertaald;
    return mergePages(vertaald, nederlands);
  });

export type LocaleStatus = {
  lang: string;
  totaal: number;
  gepubliceerd: number;
  volledig: boolean;
  ontbrekend: string[];
};

/**
 * Welke talen volledig gepubliceerd zijn. Dat volgt uit de inhoud zelf:
 * een taal telt pas mee wanneer elke gepubliceerde Nederlandse pagina ook in die taal gepubliceerd is.
 */
export const getLocaleReadiness = createServerFn({ method: "GET" }).handler(async (): Promise<LocaleStatus[]> => {
  const { data, error } = await publicClient().from("content_pages").select("sleutel, lang, status");
  if (error) throw new Error(error.message);
  const rows = (data ?? []).filter((row) => row.status === "gepubliceerd");
  const nederlands = rows.filter((row) => row.lang === "nl").map((row) => row.sleutel);
  return ["en", "fr"].map((lang) => {
    const aanwezig = new Set(rows.filter((row) => row.lang === lang).map((row) => row.sleutel));
    const ontbrekend = nederlands.filter((sleutel) => !aanwezig.has(sleutel)).sort();
    return {
      lang,
      totaal: nederlands.length,
      gepubliceerd: nederlands.length - ontbrekend.length,
      volledig: nederlands.length > 0 && ontbrekend.length === 0,
      ontbrekend,
    };
  });
});

export type TranslationSectionStatus = {
  sleutel: string;
  vertaald: boolean;
  verouderd: boolean;
  bronGewijzigdOp: string;
  vertaaldOp: string;
};

export type TranslationStatus = {
  lang: string;
  bestaat: boolean;
  status: "concept" | "gepubliceerd";
  gepubliceerdOp: string;
  totaal: number;
  vertaald: number;
  terugval: number;
  verouderd: number;
  secties: TranslationSectionStatus[];
};

/** Vertaalstand van één pagina: hoeveel secties vertaald zijn en welke mogelijk verouderd zijn. */
export const adminTranslationStatus = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { sleutel: string }) => ({ sleutel: String(data.sleutel) }))
  .handler(async ({ data, context }): Promise<TranslationStatus[]> => {
    await assertActiveUser(context);
    const bron = await loadPage(context.supabase, data.sleutel, "nl", false);
    const talen = ["en", "fr"] as const;
    const resultaat: TranslationStatus[] = [];
    for (const lang of talen) {
      const page = await loadPage(context.supabase, data.sleutel, lang, false);
      const secties = (bron?.sections ?? []).map((section) => {
        const eigen = page?.sections.find((row) => row.sleutel === section.sleutel);
        const gevuld = Boolean(eigen && (eigen.titel.trim() !== "" || eigen.tekst.trim() !== ""));
        return {
          sleutel: section.sleutel,
          vertaald: gevuld,
          verouderd: gevuld && Boolean(eigen) && section.updatedAt > eigen!.updatedAt,
          bronGewijzigdOp: section.updatedAt,
          vertaaldOp: eigen?.updatedAt ?? "",
        } satisfies TranslationSectionStatus;
      });
      resultaat.push({
        lang,
        bestaat: Boolean(page),
        status: page?.status ?? "concept",
        gepubliceerdOp: page?.status === "gepubliceerd" ? page.updatedAt : "",
        totaal: secties.length,
        vertaald: secties.filter((section) => section.vertaald).length,
        terugval: secties.filter((section) => !section.vertaald).length,
        verouderd: secties.filter((section) => section.verouderd).length,
        secties,
      });
    }
    return resultaat;
  });


export type ContentPageRow = {
  sleutel: string;
  lang: string;
  status: "concept" | "gepubliceerd";
  updatedAt: string;
  uitDatabank: boolean;
};

/** Overzicht van alle pagina's voor het beheer, met terugvalstatus. */
export const adminListPages = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<ContentPageRow[]> => {
    await assertActiveUser(context);
    const { data, error } = await context.supabase
      .from("content_pages")
      .select("sleutel, lang, status, updated_at")
      .order("sleutel");
    if (error) throw new Error(error.message);
    return (data ?? []).map((row) => ({
      sleutel: row.sleutel,
      lang: row.lang,
      status: row.status === "gepubliceerd" ? "gepubliceerd" : "concept",
      updatedAt: row.updated_at,
      uitDatabank: row.status === "gepubliceerd",
    }));
  });

/** Eén pagina met al haar secties, concept inbegrepen. */
export const adminGetPage = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { sleutel: string; lang?: string }) => ({
    sleutel: String(data.sleutel),
    lang: data.lang ?? "nl",
  }))
  .handler(async ({ data, context }): Promise<ContentPage | null> => {
    await assertActiveUser(context);
    return loadPage(context.supabase, data.sleutel, data.lang, false);
  });

export type ContentSectionInput = {
  id?: string;
  sleutel: string;
  sortOrder: number;
  type: string;
  overline: string;
  titel: string;
  tekst: string;
  opsomming: string[];
  knopTekst: string;
  knopLink: string;
  knop2Tekst: string;
  knop2Link: string;
  mediaId: string | null;
  alt: string;
  /** Fotoreeks bij dit blok, in volgorde. */
  media?: SectionMedia[];
};

export type ContentPageInput = {
  sleutel: string;
  lang: string;
  status: "concept" | "gepubliceerd";
  metaTitle: string;
  metaDescription: string;
  ogMediaId: string | null;
  indexeerbaar: boolean;
  sections: ContentSectionInput[];
};

function readableError(message: string) {
  if (message.includes("publiceren")) return message;
  if (message.includes("row-level security")) {
    return "U hebt geen recht om deze pagina te bewerken. Vraag dit aan een beheerder.";
  }
  return message;
}

/** De fotoreeks van één blok volledig vervangen, in de volgorde van het beheer. */
async function saveSectionMedia(client: AnyClient, sectionId: string, media: SectionMedia[]) {
  const { error: deleteError } = await client.from("content_section_media").delete().eq("section_id", sectionId);
  if (deleteError) throw new Error(readableError(deleteError.message));
  if (media.length === 0) return;
  const { error } = await client.from("content_section_media").insert(
    media.map((foto, index) => ({
      section_id: sectionId,
      media_id: foto.mediaId,
      sort_order: index,
      alt: foto.alt.trim(),
      caption: foto.caption.trim(),
    })),
  );
  if (error) throw new Error(readableError(error.message));
}

/** Pagina en secties bewaren. Publiceren wordt in de databank getoetst. */
export const savePage = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: ContentPageInput) => data)
  .handler(async ({ data, context }) => {
    await assertActiveUser(context);
    const tekstFouten = data.sections.flatMap((section) =>
      valideerWaarde(section.tekst, section.type === "hero" ? "kort" : "pagina", `Blok "${section.sleutel}"`),
    );
    if (tekstFouten.length > 0) throw new Error(tekstFouten.join(" "));
    for (const section of data.sections) {
      if (section.mediaId && section.alt.trim() === "") {
        throw new Error(`Geef een beschrijving bij de foto van de sectie "${section.sleutel}".`);
      }
      for (const foto of section.media ?? []) {
        if (foto.alt.trim() === "") {
          throw new Error(`Geef een beschrijving bij elke foto in de reeks van de sectie "${section.sleutel}".`);
        }
      }
    }

    const { data: existing, error: readError } = await context.supabase
      .from("content_pages")
      .select("id")
      .eq("sleutel", data.sleutel)
      .eq("lang", data.lang)
      .maybeSingle();
    if (readError) throw new Error(readableError(readError.message));

    const payload = {
      sleutel: data.sleutel,
      lang: data.lang,
      status: data.status,
      meta_title: data.metaTitle.trim(),
      meta_description: data.metaDescription.trim(),
      og_media_id: data.ogMediaId,
      indexeerbaar: data.indexeerbaar,
      updated_by: context.userId,
    };

    let pageId = existing?.id ?? null;
    if (pageId) {
      const { error } = await context.supabase.from("content_pages").update(payload).eq("id", pageId);
      if (error) throw new Error(readableError(error.message));
    } else {
      const { data: inserted, error } = await context.supabase
        .from("content_pages")
        .insert({ ...payload, created_by: context.userId })
        .select("id")
        .single();
      if (error) throw new Error(readableError(error.message));
      pageId = inserted.id;
    }

    for (const section of data.sections) {
      const sectionPayload = {
        page_id: pageId,
        sleutel: section.sleutel,
        sort_order: section.sortOrder,
        type: section.type,
        overline: section.overline,
        titel: section.titel,
        tekst: section.tekst,
        opsomming: section.opsomming.filter((item) => item.trim() !== ""),
        knop_tekst: section.knopTekst,
        knop_link: section.knopLink,
        knop2_tekst: section.knop2Tekst,
        knop2_link: section.knop2Link,
        media_id: section.mediaId,
        alt: section.alt,
      };
      const { data: bewaard, error } = await context.supabase
        .from("content_sections")
        .upsert(sectionPayload, { onConflict: "page_id,sleutel" })
        .select("id")
        .single();
      if (error) throw new Error(readableError(error.message));
      await saveSectionMedia(context.supabase, bewaard.id, section.media ?? []);
    }

    if (data.status === "gepubliceerd") {
      const snapshot = JSON.parse(JSON.stringify({ page: payload, sections: data.sections })) as Json;
      await context.supabase
        .from("content_revisions")
        .insert({ page_id: pageId, snapshot, reden: "Gepubliceerd", created_by: context.userId });
    }

    return { ok: true };
  });

export type ContentRevision = { id: string; createdAt: string; reden: string };

/** Versiegeschiedenis van één pagina. */
export const listRevisions = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { pageId: string }) => ({ pageId: String(data.pageId) }))
  .handler(async ({ data, context }): Promise<ContentRevision[]> => {
    await assertActiveUser(context);
    const { data: rows, error } = await context.supabase
      .from("content_revisions")
      .select("id, created_at, reden")
      .eq("page_id", data.pageId)
      .order("created_at", { ascending: false })
      .limit(25);
    if (error) throw new Error(error.message);
    return (rows ?? []).map((row) => ({ id: row.id, createdAt: row.created_at, reden: row.reden }));
  });

/** Een eerdere versie terugzetten als concept; publiceren blijft een aparte handeling. */
export const restoreRevision = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { revisionId: string }) => ({ revisionId: String(data.revisionId) }))
  .handler(async ({ data, context }) => {
    await assertActiveUser(context);
    const { data: revision, error } = await context.supabase
      .from("content_revisions")
      .select("page_id, snapshot")
      .eq("id", data.revisionId)
      .single();
    if (error) throw new Error(readableError(error.message));

    const snapshot = revision.snapshot as { page?: Record<string, unknown>; sections?: ContentSectionInput[] };
    const page = snapshot.page ?? {};
    const { error: pageError } = await context.supabase
      .from("content_pages")
      .update({
        meta_title: String(page["meta_title"] ?? ""),
        meta_description: String(page["meta_description"] ?? ""),
        indexeerbaar: page["indexeerbaar"] !== false,
        status: "concept",
        updated_by: context.userId,
      })
      .eq("id", revision.page_id);
    if (pageError) throw new Error(readableError(pageError.message));

    for (const section of snapshot.sections ?? []) {
      const { data: bewaard, error: sectionError } = await context.supabase.from("content_sections").upsert(
        {
          page_id: revision.page_id,
          sleutel: section.sleutel,
          sort_order: section.sortOrder,
          type: section.type,
          overline: section.overline,
          titel: section.titel,
          tekst: section.tekst,
          opsomming: section.opsomming ?? [],
          knop_tekst: section.knopTekst,
          knop_link: section.knopLink,
          knop2_tekst: section.knop2Tekst,
          knop2_link: section.knop2Link,
          media_id: section.mediaId,
          alt: section.alt,
        },
          { onConflict: "page_id,sleutel" },
        )
        .select("id")
        .single();
      if (sectionError) throw new Error(readableError(sectionError.message));
      if (section.media) await saveSectionMedia(context.supabase, bewaard.id, section.media);
    }
    return { ok: true };
  });
